import { useState } from "react";
import { readSheet } from "read-excel-file/browser";
import type {
  InventoryItem,
  Supplier,
  SupplierImportChoice,
  SupplierImportPlanRow,
  SupplierImportPreview,
  SupplierImportRequest,
  SupplierImportRow,
} from "@dentacore/shared-types";

import { formatMoney } from "../patients/money";
import { Modal } from "../ui/modal";
import { cellText } from "./supplier-text";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * The names a column may go by. The file's first row is matched against these,
 * ignoring case, spacing and anything in brackets — "Price (USD)" is Price.
 */
const COLUMNS: Record<keyof Omit<SupplierImportRow, "row">, readonly string[]> = {
  item: ["item", "item name", "name", "product", "description", "material"],
  pack: ["pack", "pack size", "packaging", "unit", "pack description"],
  uses: [
    "uses per pack",
    "uses",
    "uses in one pack",
    "quantity per pack",
    "qty per pack",
    "units per pack",
    "pieces per pack",
  ],
  price: ["price", "price per pack", "unit price", "cost", "pack price"],
  days: ["days to arrive", "days", "lead time", "delivery days", "delivery"],
};
const REQUIRED: readonly [keyof typeof COLUMNS, string][] = [
  ["item", "Item"],
  ["uses", "Uses per pack"],
  ["price", "Price"],
];

const header = (cell: string): string =>
  cell
    .toLowerCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** A CSV line reader that respects quoted commas — `"Gloves, medium"` is one cell. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += c ?? "";
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

/** The first sheet as rows of text. Excel through `read-excel-file`, CSV by hand (SU-D15). */
async function readFile(file: File): Promise<string[][]> {
  if (/\.csv$/i.test(file.name) || file.type === "text/csv") {
    return parseCsv(await file.text());
  }
  if (/\.xls$/i.test(file.name)) {
    throw new Error("That is the old .xls format. Open it in Excel and save it as .xlsx or .csv.");
  }
  // Numbers kept as the text Excel stored, so a price never passes through a float here.
  const sheet = await readSheet(file, { parseNumber: (text: string) => text });
  return sheet.map((row) => row.map(cellText));
}

/** Turn a sheet into rows the server can plan, or say what the first row lacks. */
function toRows(table: string[][]): SupplierImportRow[] {
  const [head, ...body] = table;
  if (head === undefined) throw new Error("That file is empty.");
  const names = head.map(header);
  const index = Object.fromEntries(
    Object.entries(COLUMNS).map(([key, aliases]) => [
      key,
      names.findIndex((n) => aliases.includes(n)),
    ]),
  ) as Record<keyof typeof COLUMNS, number>;
  const missing = REQUIRED.filter(([key]) => index[key] === -1).map(([, label]) => label);
  if (missing.length > 0) {
    throw new Error(
      `The first row needs a column called ${missing.join(", ")}. Found: ${head.filter((h) => h.trim() !== "").join(", ") || "nothing"}.`,
    );
  }
  const read = (cells: string[], key: keyof typeof COLUMNS): string =>
    index[key] === -1 ? "" : (cells[index[key]] ?? "").trim();
  const rows = body
    .map((cells, offset) => ({
      // The header is line 1, so the first data row is line 2 — the number on screen in Excel.
      row: offset + 2,
      item: read(cells, "item"),
      pack: read(cells, "pack"),
      uses: read(cells, "uses"),
      price: read(cells, "price"),
      days: read(cells, "days"),
    }))
    .filter((r) => [r.item, r.pack, r.uses, r.price, r.days].some((v) => v !== ""));
  if (rows.length === 0) throw new Error("Found the column names but no rows under them.");
  return rows;
}

/**
 * *Import supplier items from file* — STORY-171 (SU-D10, SU-D13, SU-D15).
 *
 * One supplier's list at a time. **Nothing changes until the preview is
 * confirmed**, and the preview is the server's own plan, so it cannot promise
 * what the import then does differently. A name that matches no item anywhere
 * in the clinic waits for a choice: create it here, treat it as an existing
 * item, or skip it. An import only adds and updates — never removes a price.
 */
export function ImportDialog({
  supplier,
  items,
  currency,
  onPreview,
  onImport,
  onClose,
}: {
  supplier: Supplier;
  /** For "same as an existing item". This location's; the server knows the clinic's. */
  items: readonly InventoryItem[];
  currency: string;
  onPreview: (body: SupplierImportRequest) => Promise<SupplierImportPreview>;
  /** Resolves true once imported, so the dialog closes only then. */
  onImport: (body: SupplierImportRequest) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<SupplierImportRow[] | null>(null);
  const [choices, setChoices] = useState<Record<string, SupplierImportChoice>>({});
  const [preview, setPreview] = useState<SupplierImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const plan = async (
    next: SupplierImportRow[],
    picked: Record<string, SupplierImportChoice>,
  ): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      setPreview(await onPreview({ rows: next, choices: picked }));
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "The file could not be checked.");
    } finally {
      setWorking(false);
    }
  };

  const choose = (row: SupplierImportPlanRow, value: string): void => {
    if (rows === null) return;
    const next = { ...choices };
    if (value === "") delete next[String(row.row)];
    else if (value === "create") next[String(row.row)] = { kind: "create" };
    else if (value === "skip") next[String(row.row)] = { kind: "skip" };
    else next[String(row.row)] = { kind: "match", itemName: value.slice("match:".length) };
    setChoices(next);
    void plan(rows, next);
  };

  const choiceValue = (row: SupplierImportPlanRow): string => {
    const picked = choices[String(row.row)];
    if (picked === undefined) return "";
    return picked.kind === "match" ? `match:${picked.itemName}` : picked.kind;
  };

  const title = `Import ${supplier.name}'s items from a file`;
  const count = (outcomes: string[]): number =>
    preview?.rows.filter((r) => outcomes.includes(r.outcome)).length ?? 0;

  const what = (row: SupplierImportPlanRow): React.JSX.Element => {
    const unknownName = row.outcome === "unknown" || choices[String(row.row)] !== undefined;
    const picker = unknownName ? (
      <select
        className={styles.choice}
        value={choiceValue(row)}
        onChange={(e) => choose(row, e.target.value)}
        aria-label={`What to do with ${row.itemName}`}
        data-testid={`import-choice-${String(row.row)}`}
      >
        <option value="">Choose…</option>
        {row.suggestion !== null && (
          <option value={`match:${row.suggestion}`}>Same as {row.suggestion} (suggested)</option>
        )}
        <option value="create">Add as a new item here</option>
        <optgroup label="Same as another item">
          {items
            .filter((i) => i.name !== row.suggestion)
            .map((i) => (
              <option key={i.id} value={`match:${i.name}`}>
                {i.name}
              </option>
            ))}
        </optgroup>
        <option value="skip">Skip this row</option>
      </select>
    ) : null;
    const chip = (kind: string, text: string): React.JSX.Element => (
      <span className={`${styles.chip} ${kind}`}>{text}</span>
    );
    switch (row.outcome) {
      case "add":
        return (
          <>
            {picker}
            {chip(styles.chipBest ?? "", "New price")}
          </>
        );
      case "change":
        return (
          <>
            {picker}
            {chip(styles.chipOrder ?? "", "Price change")}{" "}
            {row.previous !== null && row.priceCents !== null && (
              <span>
                {formatMoney(row.previous.priceCents, currency)} →{" "}
                {formatMoney(row.priceCents, currency)}
              </span>
            )}
            {row.previous !== null &&
              (row.previous.pack !== row.pack || row.previous.usesPerPack !== row.usesPerPack) && (
                <span className={styles.meta}>
                  Pack was {row.previous.pack} · {row.previous.usesPerPack} uses
                </span>
              )}
          </>
        );
      case "same":
        return (
          <>
            {picker}
            {chip(styles.chipPlain ?? "", "No change")}
          </>
        );
      case "create":
        return (
          <>
            {picker}
            {chip(styles.chipBest ?? "", "New item")}{" "}
            <span className={styles.meta}>Added here; one pack counts as full</span>
          </>
        );
      case "unknown":
        return (
          <>
            {chip(styles.chipWarm ?? "", "Not in Inventory")} {picker}
          </>
        );
      case "skip":
      case "duplicate":
        return (
          <>
            {picker}
            {chip(styles.chipPlain ?? "", "Skipped")}{" "}
            {row.note !== null && <span className={styles.meta}>{row.note}</span>}
          </>
        );
      default:
        return (
          <>
            {chip(styles.chipWarm ?? "", "Can't import")}{" "}
            <span className={styles.meta}>{row.note}</span>
          </>
        );
    }
  };

  return (
    <Modal label={title} onClose={onClose} testId="import-dialog" panelClassName={styles.wide}>
      <div className={inv.dialogForm}>
        <h2 className={inv.dialogTitle}>{title}</h2>

        {preview === null ? (
          <>
            <p className={styles.sub}>
              Nothing changes until you have seen the preview and confirmed it.
            </p>
            <label className={inv.field}>
              <span className={inv.fieldLabel}>Excel (.xlsx) or CSV file</span>
              <input
                type="file"
                accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={working}
                data-testid="import-file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file === undefined) return;
                  setFileName(file.name);
                  setError(null);
                  setChoices({});
                  readFile(file)
                    .then((table) => {
                      const next = toRows(table);
                      setRows(next);
                      return plan(next, {});
                    })
                    .catch((cause: unknown) => {
                      setRows(null);
                      setError(
                        cause instanceof Error ? cause.message : "That file could not be read.",
                      );
                    });
                }}
              />
            </label>
            <div>
              <p className={styles.note}>
                The first sheet is read. Its first row must name the columns, in any order:
              </p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>What goes in it</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Item</td>
                      <td>The item&apos;s name</td>
                      <td className={styles.meta}>
                        Required
                        <RequiredMark />
                      </td>
                    </tr>
                    <tr>
                      <td>Uses per pack</td>
                      <td>How many uses one pack holds</td>
                      <td className={styles.meta}>
                        Required
                        <RequiredMark />
                      </td>
                    </tr>
                    <tr>
                      <td>Price</td>
                      <td>The price of one pack</td>
                      <td className={styles.meta}>
                        Required
                        <RequiredMark />
                      </td>
                    </tr>
                    <tr>
                      <td>Pack</td>
                      <td>What their pack is called</td>
                      <td className={styles.meta}>&mdash;</td>
                    </tr>
                    <tr>
                      <td>Days to arrive</td>
                      <td>A whole number of days</td>
                      <td className={styles.meta}>&mdash;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className={styles.sub}>
              {fileName} · {rows?.length ?? 0} rows read
            </p>
            <div className={styles.summary}>
              {[
                ["New prices", count(["add"])],
                ["Price changes", count(["change"])],
                ["New items", count(["create"])],
                ["No change", count(["same"])],
                ["Skipped", count(["skip", "duplicate", "error"])],
                ["Need a choice", preview.undecided],
              ]
                .filter(([, n]) => n !== 0)
                .map(([label, n]) => (
                  <span
                    key={label}
                    className={`${styles.chip} ${label === "Need a choice" ? styles.chipWarm : styles.chipPlain}`}
                  >
                    {label}: {n}
                  </span>
                ))}
            </div>
            <div className={`${styles.tableWrap} ${styles.previewArea}`}>
              <table className={styles.table} data-testid="import-preview">
                <thead>
                  <tr>
                    <th className={styles.num}>Row</th>
                    <th>In the file</th>
                    <th className={styles.num}>Price</th>
                    <th>What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr key={row.row}>
                      <td className={styles.num}>{row.row}</td>
                      <td>
                        {row.itemName || "—"}
                        <span className={styles.meta}>
                          {row.pack}
                          {row.usesPerPack !== null && ` · ${String(row.usesPerPack)} uses`}
                        </span>
                      </td>
                      <td className={styles.num}>
                        {row.priceCents === null ? "—" : formatMoney(row.priceCents, currency)}
                      </td>
                      <td>{what(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {count(["create"]) > 0 && (
              <p className={styles.note}>
                New items count one of this supplier's packs as a full shelf until you set one in
                Inventory.
              </p>
            )}
          </>
        )}

        {error !== null && <p className={styles.error}>{error}</p>}

        <div className={inv.dialogActions}>
          {preview !== null && (
            <button
              type="button"
              className={`${inv.quiet} ${styles.pushLeft}`}
              onClick={() => {
                setPreview(null);
                setRows(null);
                setChoices({});
                setFileName(null);
              }}
            >
              Pick another file
            </button>
          )}
          <button type="button" className={inv.ghost} onClick={onClose}>
            Cancel
          </button>
          {preview !== null && rows !== null && (
            <button
              type="button"
              className={inv.primary}
              disabled={working || preview.undecided > 0 || preview.writes === 0}
              data-testid="import-confirm"
              onClick={() => {
                setWorking(true);
                void onImport({ rows, choices }).then((landed) => {
                  setWorking(false);
                  if (landed) onClose();
                });
              }}
            >
              {preview.undecided > 0
                ? `Choose for ${String(preview.undecided)} row${preview.undecided === 1 ? "" : "s"} first`
                : preview.writes === 0
                  ? "Nothing to import"
                  : `Import ${String(preview.writes)} row${preview.writes === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
